import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Grid,
  FormControlLabel,
  Switch,
  Typography,
  IconButton,
  Divider
} from '@mui/material';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { Tab } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
// Remove date picker imports - will use native datetime input

const EditCampaignDialog = ({ open, onClose, campaign, onSave }) => {
  const [activeTab, setActiveTab] = useState('1');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const [formData, setFormData] = useState({
    name_en: '',
    name_fi: '',
    name_fr: '',
    name_de: '',
    color: '#FF6347',
    start_date: '',
    end_date: '',
    is_active: 1
  });

  const [rules, setRules] = useState([
    { min_price: '', max_price: '', discount_percentage: '' }
  ]);

  const languages = [
    { code: 'en', label: 'English' },
    { code: 'fi', label: 'Finnish' },
    { code: 'fr', label: 'French' },
    { code: 'de', label: 'German' }
  ];

  useEffect(() => {
    if (campaign) {
      setFormData({
        name_en: campaign.name_en || '',
        name_fi: campaign.name_fi || '',
        name_fr: campaign.name_fr || '',
        name_de: campaign.name_de || '',
        color: campaign.color || '#FF6347',
        start_date: campaign.start_date ? formatDateForInput(campaign.start_date) : '',
        end_date: campaign.end_date ? formatDateForInput(campaign.end_date) : '',
        is_active: campaign.is_active === '1' || campaign.is_active === 1 ? 1 : 0
      });

      if (campaign.rules && campaign.rules.length > 0) {
        setRules(campaign.rules.map(rule => ({
          min_price: rule.min_price || '',
          max_price: rule.max_price || '',
          discount_percentage: rule.discount_percentage || ''
        })));
      } else {
        setRules([{ min_price: '', max_price: '', discount_percentage: '' }]);
      }

      setError('');
      setSuccess(false);
      setActiveTab('1');
    }
  }, [campaign]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };


  const handleRuleChange = (index, field, value) => {
    const newRules = [...rules];
    newRules[index][field] = value;
    setRules(newRules);
  };

  const addRule = () => {
    setRules([...rules, { min_price: '', max_price: '', discount_percentage: '' }]);
  };

  const removeRule = (index) => {
    if (rules.length > 1) {
      setRules(rules.filter((_, i) => i !== index));
    }
  };

  const validateRules = () => {
    for (let i = 0; i < rules.length; i++) {
      const rule = rules[i];
      if (!rule.min_price || !rule.max_price || !rule.discount_percentage) {
        return 'All rule fields must be filled';
      }
      if (parseFloat(rule.min_price) >= parseFloat(rule.max_price)) {
        return `Rule ${i + 1}: Min price must be less than max price`;
      }
      if (parseFloat(rule.discount_percentage) < 0 || parseFloat(rule.discount_percentage) > 100) {
        return `Rule ${i + 1}: Discount must be between 0 and 100`;
      }
    }
    return null;
  };

  const formatDateForAPI = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = '00';
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  const handleSubmit = async () => {
    if (!campaign) return;

    // Validation
    if (!formData.name_en && !formData.name_fi && !formData.name_fr && !formData.name_de) {
      setError('At least one campaign name is required');
      return;
    }

    const ruleError = validateRules();
    if (ruleError) {
      setError(ruleError);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const payload = {
        id: campaign.id,
        name_en: formData.name_en || null,
        name_fi: formData.name_fi || null,
        name_fr: formData.name_fr || null,
        name_de: formData.name_de || null,
        color: formData.color,
        start_date: formatDateForAPI(formData.start_date),
        end_date: formatDateForAPI(formData.end_date),
        is_active: formData.is_active,
        rules: rules.map(rule => ({
          min_price: parseFloat(rule.min_price).toFixed(2),
          max_price: parseFloat(rule.max_price).toFixed(2),
          discount_percentage: parseFloat(rule.discount_percentage).toFixed(2)
        }))
      };

      console.log('Sending update payload:', payload);

      const response = await fetch('https://api.elnaf.fi/api/update_campaign.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const responseText = await response.text();
      console.log('Response:', responseText);
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        console.error('Failed to parse JSON:', e);
        setError('Invalid response from server');
        setLoading(false);
        return;
      }

      if (response.ok && (data.status === 'success' || data.message?.includes('uğurla'))) {
        setSuccess(true);
        setTimeout(() => {
          onSave();
        }, 1500);
      } else {
        setError(data.message || 'Failed to update campaign');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading && !success) {
      setError('');
      setSuccess(false);
      setActiveTab('1');
      onClose();
    }
  };

  if (!campaign) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Edit Campaign
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          ID: {campaign.id}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Campaign updated successfully!
            </Alert>
          )}

          <TabContext value={activeTab}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <TabList onChange={handleTabChange}>
                <Tab label="Campaign Names" value="1" />
                <Tab label="Settings & Rules" value="2" />
              </TabList>
            </Box>

            <TabPanel value="1">
              <Grid container spacing={2}>
                {languages.map((lang) => (
                  <Grid item xs={12} sm={6} key={lang.code}>
                    <TextField
                      fullWidth
                      label={`Campaign Name (${lang.label})`}
                      name={`name_${lang.code}`}
                      value={formData[`name_${lang.code}`]}
                      onChange={handleChange}
                      variant="outlined"
                    />
                  </Grid>
                ))}
              </Grid>
            </TabPanel>

            <TabPanel value="2">
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Color"
                    name="color"
                    type="color"
                    value={formData.color}
                    onChange={handleChange}
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_active === 1}
                        onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked ? 1 : 0 }))}
                      />
                    }
                    label="Active"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    name="start_date"
                    type="datetime-local"
                    value={formData.start_date}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="End Date"
                    name="end_date"
                    type="datetime-local"
                    value={formData.end_date}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">Discount Rules</Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={addRule}
                    variant="outlined"
                  >
                    Add Rule
                  </Button>
                </Box>

                {rules.map((rule, index) => (
                  <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="subtitle2">Rule {index + 1}</Typography>
                      {rules.length > 1 && (
                        <IconButton
                          size="small"
                          onClick={() => removeRule(index)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Min Price"
                          type="number"
                          value={rule.min_price}
                          onChange={(e) => handleRuleChange(index, 'min_price', e.target.value)}
                          InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Max Price"
                          type="number"
                          value={rule.max_price}
                          onChange={(e) => handleRuleChange(index, 'max_price', e.target.value)}
                          InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Discount %"
                          type="number"
                          value={rule.discount_percentage}
                          onChange={(e) => handleRuleChange(index, 'discount_percentage', e.target.value)}
                          InputProps={{ inputProps: { min: 0, max: 100, step: 0.01 } }}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Box>
            </TabPanel>
          </TabContext>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading || success}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || success}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {success ? 'Saved!' : 'Update Campaign'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditCampaignDialog;